export interface ElfEmergencyGuideOption {
  title: string;
  description: string;
  steps: string[];
}

export interface ElfEmergencyGuide {
  slug: string;
  existingArticlePath?: string;
  title: string;
  description: string;
  content: {
    introduction: string;
    directAnswer: string;
    options: ElfEmergencyGuideOption[];
    copyableExample: string;
    scenario: {
      title: string;
      body: string;
    };
  };
  relatedLinks: Array<{
    href: string;
    label: string;
  }>;
}

const arrivalLinks = [
  {
    href: "/blog/free-elf-arrival-letter-templates-2026/",
    label: "Free printable elf arrival letters",
  },
  {
    href: "/blog/creative-holiday-elf-arrival-ideas-2026/",
    label: "Creative holiday elf arrival ideas",
  },
];

const goodbyeLink = {
  href: "/blog/holiday-elf-departure-letters-guide/",
  label: "The holiday elf departure letter guide",
};

export const ELF_EMERGENCY_GUIDES: ElfEmergencyGuide[] = [
  {
    slug: "forgot-to-move",
    title: "Forgot to Move the Elf? 3 Easy Fixes for This Morning",
    description:
      "Forgot to move your Christmas elf? Use three calm, quick ideas for a believable note and an easy reset before school.",
    content: {
      introduction:
        "Some nights run long, and the elf stays exactly where you left them. You can still create a sweet moment before the day gets moving.",
      directAnswer:
        "If your child has not seen the elf yet, move it now and keep the scene simple. If your child already noticed it stayed put, leave it exactly where it is and use a playful note instead—never move it in front of them. A confident, cozy explanation is enough.",
      options: [
        {
          title: "Move it before anyone sees",
          description:
            "Move the elf to a nearby spot that takes less than a minute to arrange. A mug, mitten, book, or ribbon can make the new perch feel intentional.",
          steps: [
            "Choose a safe, visible spot away from heat, pets, and anything a child could pull down.",
            "Add one small prop from the room so the scene feels finished.",
            "Leave the elf there and let the simple change speak for itself.",
          ],
        },
        {
          title: "Let the elf explain staying put",
          description:
            "If your child already saw the elf in the original spot, leave it there. A two-sentence message about getting comfortable with a good book makes staying put feel like a choice.",
          steps: [
            "Do not move the elf while your child is watching or after they have called attention to it.",
            "Place a note beside the elf, or hand it over as part of the morning conversation.",
            "Use your usual elf name and sign-off for a familiar feeling.",
          ],
        },
        {
          title: "Make the child part of it",
          description:
            "If your child notices the elf was late, invite them to choose a cozy place for tomorrow's scene. The tradition becomes shared play instead of a missed assignment.",
          steps: [
            "Say the elf needed help finding a good spot for the next visit.",
            "Let your child pick between two safe places.",
            "Thank them for helping the elf settle in.",
          ],
        },
      ],
      copyableExample:
        "Hi! I stayed up late reading the holiday storybook and decided this bookshelf was the coziest place to rest. I’ll see you soon! Love, Your Elf",
      scenario: {
        title: "A five-minute save before the bus",
        body:
          "In this fictional family example, parent Casey notices at 6:40 a.m. that Pickle is still on the bookshelf. Casey leaves Pickle exactly where child Jordan already spotted the elf, adds the short reading note, and gets everyone out the door on time without a last-second move.",
      },
    },
    relatedLinks: [...arrivalLinks, goodbyeLink],
  },
  {
    slug: "late-arrival",
    title: "Late Elf Arrival? A No-Stress Way to Start Tonight",
    description:
      "A late Christmas elf arrival can still feel magical. Choose a simple welcome, note, or scene you can set up tonight.",
    content: {
      introduction:
        "The first visit can happen when your family is ready. A later arrival gives you room to make the welcome fit this year’s real schedule.",
      directAnswer:
        "Start tonight with a warm welcome and a short reason for the delay, or skip the reason entirely. Children need a clear beginning more than a perfect calendar date.",
      options: [
        {
          title: "Give the elf a warm welcome",
          description:
            "Place the elf near the breakfast table, tree, or another familiar family spot so the first morning feels connected to your routine.",
          steps: [
            "Pick a place your child naturally looks in the morning.",
            "Add a drawing, candy cane, or favorite book instead of a complicated scene.",
            "Say, ‘I’m glad you’re here,’ and let the visit begin.",
          ],
        },
        {
          title: "Explain the travel day",
          description:
            "If your child asks why the elf came later, keep the answer brief: travel took longer, the elf was helping at the North Pole, or tonight was the right time for your home.",
          steps: [
            "Choose one explanation that feels natural for your family.",
            "Write it in a sentence or two rather than building a complicated backstory.",
            "End with excitement about the time you will share together.",
          ],
        },
        {
          title: "Start with one small tradition",
          description:
            "A shared cup of cocoa, a bedtime read, or a first-night photo gives the arrival a clear place in your family’s season.",
          steps: [
            "Choose an activity you already have time for tonight.",
            "Let the elf ‘invite’ your child with a note or a tiny prop.",
            "Keep tomorrow open so the tradition can grow at your pace.",
          ],
        },
      ],
      copyableExample:
        "Hello, friend! My trip took a little longer than planned, but I made it here safely. I’m so glad to begin our Christmas fun together. Love, Your Elf",
      scenario: {
        title: "A welcome after a busy week",
        body:
          "Elena’s family planned an arrival for December 1, but a week of illness changed their schedule. On Friday night, they place Pepper beside the tree with a drawing and the short note above. Elena wakes up to a calm, happy beginning that fits the family’s week.",
      },
    },
    relatedLinks: [...arrivalLinks, goodbyeLink],
  },
  {
    slug: "touched-elf",
    existingArticlePath: "/blog/accidentally-touched-holiday-elf-restore-magic-guide/",
    title: "Your Child Touched the Elf: What to Say Next",
    description:
      "If your child touched the elf, stay calm. Find kind words and three easy ways to keep the tradition feeling magical.",
    content: {
      introduction:
        "A curious hand, a fall from a shelf, or a big hug can happen. One touch does not have to turn a playful tradition into a stressful moment.",
      directAnswer:
        "Nothing is ruined, and your child did not do anything wrong. Reassure them, then let the elf tradition continue as usual. Any rest scene or note can be optional imaginative play, never a consequence.",
      options: [
        {
          title: "Offer a cozy rest scene",
          description:
            "If your child enjoys the story, place the elf with a blanket, paper star, or small cup nearby. The scene is a fun idea, not something the elf needs because of the touch.",
          steps: [
            "Start by saying clearly that nothing is ruined and your child did nothing wrong.",
            "Offer one optional prop and let your child decide whether to play along.",
            "Leave the elf as it is if your family would rather keep the morning simple.",
          ],
        },
        {
          title: "Keep the tradition flexible",
          description:
            "Use the moment to remind your child that the elf tradition is for fun. Your family can choose a simple future plan without making the touch feel like a mistake.",
          steps: [
            "Acknowledge the hug, curiosity, or helping hand with warmth.",
            "If useful, agree on a practical way to move the elf safely next time.",
            "Offer a no-touch way to play only if your child wants another option.",
          ],
        },
        {
          title: "Let curiosity become play",
          description:
            "A drawing, question, or whispered goodnight can extend the story if that sounds fun. There is nothing to repair and no apology required.",
          steps: [
            "Ask whether your child wants to add anything to the elf’s story.",
            "Choose an optional small action together, or simply carry on with the day.",
            "Close with reassurance that the Christmas fun can continue as normal.",
          ],
        },
      ],
      copyableExample:
        "Hi! I loved our big hello. I’m going to sit here for a bit and enjoy the Christmas fun with you. Love, Your Elf",
      scenario: {
        title: "A curious hello before breakfast",
        body:
          "In this fictional family example, child Marcus picks up Twinkle after spotting the elf on the floor. Parent Riley immediately reassures him that nothing is ruined and he did nothing wrong. Marcus chooses a soft scarf for Twinkle’s chair because the cozy scene sounds fun, and the next morning continues normally.",
      },
    },
    relatedLinks: [...arrivalLinks, goodbyeLink],
  },
];

export const getEmergencyGuidePath = (guide: ElfEmergencyGuide) =>
  guide.existingArticlePath ?? `/elf-help/${guide.slug}/`;
